import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import Paper from '@mui/material/Paper';
import Input from '../../../theme/ui/Atoms/Input';
import Button from '../../../theme/ui/Atoms/Button';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useErrorMessage from '../../../shared/hooks/useErrorMessage';
import { useTranslation } from 'react-i18next';
import { login } from '../../../redux/auth/action/thunks';
import LoginHeaderLogo from './LoginHeaderLogo';
import LoginFooter from './LoginFooter';
import ContainerFloatCenter from '../../../theme/ui/Layout/ContainerFloatCenter';
import { IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { initiateLogin } from '../../../shared/util/auth/oidcUtils';
import AuthIDPAPI from '../../../service/auth/AuthIDPAPI';
import { getFeatureToggle } from '../../../shared/featureToggle';
import { useAppSelector } from '../../../redux/store.model';

interface LoginProps extends ConnectedComponentProps {}
function Login(props: LoginProps) {
  const navigate = useNavigate();
  const errorMessage = useErrorMessage();
  const { t } = useTranslation();
  const { UseOidcManagerLogin: useOidcManagerLogin } =
    useAppSelector(getFeatureToggle);

  const [searchParams] = useSearchParams();

  const [displayPassword, setDisplayPassword] = useState(false);
  const { isLoading: idpConfigLoading, data: idpConfigData } =
    AuthIDPAPI.endpoints.getIDPConfig.useQuery(undefined, {
      skip: !useOidcManagerLogin,
    });

  useEffect(() => {
    const error = searchParams.get('error');
    if (error) errorMessage(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = yup.object({
    storeId: yup.string().required(t('login.store_id_required') ?? ''),
    password: yup.string().required(t('login.password_required') ?? ''),
  });

  const formik = useFormik({
    initialValues: {
      storeId: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setDisplayPassword(false);
      return props
        .login(values)
        .then(() => {
          navigate('/operation');
        })
        .catch((error) => {
          const message =
            error.code === 'ERR_BAD_REQUEST'
              ? t('error.login_failed')
              : error.message;
          errorMessage(message);
        });
    },
  });

  const onClickAsAManager = () => {
    navigate('/login-manager');
  };

  const onClickAsAManagerSSO = () => {
    initiateLogin();
  };
  return (
    <ContainerFloatCenter>
      <LoginHeaderLogo />
      <Paper elevation={5} sx={{ padding: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Typography variant="h1">{t('login.title')}</Typography>
          <Input
            id="storeId"
            name="storeId"
            sx={{ mt: 2, mb: 2 }}
            label={t('login.store_id_label') ?? ''}
            placeholder={t('login.store_id_placeholder') ?? ''}
            required
            aria-required
            fullWidth
            value={formik.values.storeId}
            onChange={formik.handleChange}
            error={formik.touched.storeId && Boolean(formik.errors.storeId)}
            helperText={(formik.touched.storeId && formik.errors.storeId) || ''}
          />
          <Input
            id="password"
            name="password"
            sx={{ mt: 2, mb: 2 }}
            label={t('login.password')}
            placeholder={t('login.password_placeholder') ?? ''}
            type={displayPassword ? 'text' : 'password'}
            required
            aria-required
            fullWidth
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={
              (formik.touched.password && formik.errors.password) || ''
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setDisplayPassword(!displayPassword)}
                  >
                    {displayPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="text"
            size="large"
            rounded
            sx={{ mt: 4 }}
            disabled={formik.isSubmitting}
            fullWidth
            onClick={onClickAsAManager}
          >
            {t('login.login_with_manager')}
          </Button>

          {useOidcManagerLogin && (
            <Button
              variant="text"
              size="large"
              rounded
              sx={{ my: 2 }}
              disabled={formik.isSubmitting || !idpConfigData?.clientId}
              loading={idpConfigLoading}
              fullWidth
              onClick={onClickAsAManagerSSO}
            >
              {t('login.login_with_manager_sso')}
            </Button>
          )}

          <Button
            variant="contained"
            size="large"
            rounded
            sx={{ my: 2 }}
            type="submit"
            fullWidth
            loading={formik.isSubmitting}
          >
            {t('login.button_title')}
          </Button>
        </form>
      </Paper>
      <LoginFooter />
    </ContainerFloatCenter>
  );
}

const connector = connect(null, {
  login: login,
});
type ConnectedComponentProps = ConnectedProps<typeof connector>;

export default connector(Login);
